import { config } from '@/config';
import { getStoredAnalyticsAttribution } from './attribution';
import type { AnalyticsEventName, AnalyticsProperties } from '@flowqa/shared';

type PostHogClient = {
  init: (
    key: string,
    options: {
      api_host: string;
      autocapture?: boolean;
      capture_pageview?: boolean;
      loaded?: (client: PostHogClient) => void;
    },
  ) => void;
  capture: (event: string, properties?: AnalyticsProperties) => void;
  identify: (distinctId: string, properties?: AnalyticsProperties) => void;
  reset: () => void;
};

type PostHogStub = PostHogClient & {
  __SV?: number;
  _i?: unknown[];
  people?: unknown[];
  push?: (...args: unknown[]) => number;
};

type GtagProperties = Record<string, string | boolean>;

type GtagArguments =
  | [command: 'js', date: Date]
  | [command: 'config', targetId: string, properties?: GtagProperties]
  | [command: 'event', name: string, properties?: GtagProperties];

type AnalyticsWindow = Window & {
  posthog?: PostHogClient;
  dataLayer?: IArguments[];
  gtag?: (...args: GtagArguments) => void;
};

let loadStarted = false;
let initialized = false;
let googleConsentListenerRegistered = false;
let pendingEvents: Array<{ name: AnalyticsEventName; properties?: AnalyticsProperties }> = [];
let pendingIdentity: { userId: string; properties?: AnalyticsProperties } | null = null;

const getPostHog = (): PostHogClient | null =>
  typeof window !== 'undefined' && (window as AnalyticsWindow).posthog
    ? ((window as AnalyticsWindow).posthog ?? null)
    : null;

const isEnabled = (): boolean =>
  Boolean(config.analytics.posthogKey && config.analytics.posthogHost);

const isLocalDebugHost = (): boolean =>
  typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);

const createPostHogStub = (): PostHogClient => {
  const stub = [] as unknown as PostHogStub;
  stub.__SV = 1;
  stub._i = [];
  stub.people = [];
  stub.push = Array.prototype.push.bind(stub) as (...args: unknown[]) => number;
  stub.capture = (event: string, properties?: AnalyticsProperties): void => {
    stub.push?.(['capture', event, properties]);
  };
  stub.identify = (distinctId: string, properties?: AnalyticsProperties): void => {
    stub.push?.(['identify', distinctId, properties]);
  };
  stub.reset = (): void => {
    stub.push?.(['reset']);
  };
  stub.init = ((key: string, options: Parameters<PostHogClient['init']>[1]) => {
    stub._i?.push([key, options, 'posthog']);
  }) as PostHogClient['init'];
  (window as AnalyticsWindow).posthog = stub;
  return stub;
};

const flushPendingEvents = (): void => {
  const posthog = getPostHog();
  if (!posthog) return;
  if (pendingIdentity) {
    posthog.identify(pendingIdentity.userId, pendingIdentity.properties);
    pendingIdentity = null;
  }
  const events = pendingEvents;
  pendingEvents = [];
  events.forEach((event) => {
    posthog.capture(event.name, event.properties);
  });
};

const loadPostHogScript = (): void => {
  if (loadStarted || !isEnabled() || typeof document === 'undefined') return;
  loadStarted = true;
  const posthog = getPostHog() ?? createPostHogStub();
  if (!initialized) {
    posthog.init(config.analytics.posthogKey, {
      api_host: config.analytics.posthogHost,
      autocapture: false,
      capture_pageview: false,
      loaded: () => {
        initialized = true;
        flushPendingEvents();
      },
    });
  }
  const existing = document.getElementById('flowqa-posthog');
  if (existing) return;
  const script = document.createElement('script');
  script.id = 'flowqa-posthog';
  script.async = true;
  script.src = `${config.analytics.posthogHost}/static/array.js`;
  script.setAttribute('data-categories', 'analytics');
  document.head.appendChild(script);
};

const loadGoogleTagScript = (): void => {
  if (!config.analytics.googleTagId || typeof document === 'undefined') return;
  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? [];
  analyticsWindow.gtag = function gtag(): void {
    // Google's gtag loader expects the native arguments object shape.
    // eslint-disable-next-line prefer-rest-params
    analyticsWindow.dataLayer?.push(arguments);
  };
  analyticsWindow.gtag('js', new Date());
  const sendConfig = (): void => {
    const properties: GtagProperties = {
      page_location: window.location.href,
      page_title: document.title,
      ...(isLocalDebugHost() ? { debug_mode: true } : {}),
    };
    analyticsWindow.gtag?.('config', config.analytics.googleTagId, properties);
    analyticsWindow.gtag?.('event', 'page_view', {
      ...properties,
      send_to: config.analytics.googleTagId,
    });
  };
  if (!googleConsentListenerRegistered) {
    googleConsentListenerRegistered = true;
    window.addEventListener('Termly.consentSaveDone', sendConfig);
  }
  const existing = document.getElementById('flowqa-google-tag');
  if (existing) {
    sendConfig();
    return;
  }
  const script = document.createElement('script');
  script.id = 'flowqa-google-tag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    config.analytics.googleTagId,
  )}`;
  script.setAttribute('data-categories', 'analytics');
  script.onload = sendConfig;
  document.head.appendChild(script);
};

const withAttribution = (properties?: AnalyticsProperties): AnalyticsProperties => {
  const { firstTouch, latestTouch } = getStoredAnalyticsAttribution();
  return {
    ...properties,
    ...(firstTouch ? { first_touch: firstTouch } : {}),
    ...(latestTouch ? { latest_touch: latestTouch } : {}),
  };
};

export const analytics = {
  initialise: (): void => {
    try {
      loadPostHogScript();
      loadGoogleTagScript();
    } catch {
      // Analytics is best-effort and must never interrupt the app.
    }
  },
  identify: (userId: string, properties?: AnalyticsProperties): void => {
    try {
      if (!isEnabled()) return;
      const payload = withAttribution(properties);
      const posthog = getPostHog();
      if (!posthog || !initialized) {
        pendingIdentity = { userId, properties: payload };
        loadPostHogScript();
        return;
      }
      posthog.identify(userId, payload);
    } catch {
      // Analytics failures are intentionally ignored.
    }
  },
  capture: (name: AnalyticsEventName, properties?: AnalyticsProperties): void => {
    try {
      if (!isEnabled()) return;
      const payload = withAttribution(properties);
      const posthog = getPostHog();
      if (!posthog || !initialized) {
        pendingEvents.push({ name, properties: payload });
        loadPostHogScript();
        return;
      }
      posthog.capture(name, payload);
    } catch {
      // Analytics failures are intentionally ignored.
    }
  },
  reset: (): void => {
    try {
      getPostHog()?.reset();
    } catch {
      // Analytics failures are intentionally ignored.
    }
  },
};
